<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';

import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  slaMissedEvents: {
    type: Array,
    required: true,
  },
  slaDetails: {
    type: Object,
    default: () => ({}),
  },
  conversationDetails: {
    type: Object,
    default: () => ({}),
  },
});

const { SLA_MISS_TYPES } = wootConstants;
const { t, locale } = useI18n();

const formatDuration = seconds => {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '';

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const parts = [];

  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes || parts.length === 0) parts.push(`${minutes}m`);

  return parts.slice(0, 2).join(' ');
};

const formatThreshold = threshold => {
  const { time, unit } = convertSecondsToTimeUnit(threshold, {
    minute: 'm',
    hour: 'h',
    day: 'd',
  });

  return time ? `${time}${unit}` : '';
};

const formatDate = timestamp => {
  if (!timestamp) return '';

  const code = locale.value?.replace(/_/g, '-') || 'en-US';
  return new Intl.DateTimeFormat(code, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(Number(timestamp) * 1000));
};

const timingContext = computed(() => {
  const createdAt = Number(props.conversationDetails.created_at);
  if (!createdAt) return {};

  const firstReplyAt = Number(props.conversationDetails.first_reply_created_at);
  const lastActivityAt = Number(props.conversationDetails.last_activity_at);
  const waitingSince = Number(props.conversationDetails.waiting_since);
  const isOpen = props.conversationDetails.status === 'open';
  const now = Math.floor(Date.now() / 1000);

  return {
    createdAt,
    firstReplyAt,
    lastActivityAt,
    waitingSince,
    isOpen,
    now,
  };
});

const eventMetaValue = (event, key) => {
  const value = event?.meta?.[key] || event?.metadata?.[key];
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : null;
};

const thresholdForType = eventType => {
  const thresholds = {
    [SLA_MISS_TYPES.FRT]:
      props.slaDetails.frt_threshold ||
      props.slaDetails.sla_first_response_time_threshold,
    [SLA_MISS_TYPES.RT]:
      props.slaDetails.rt_threshold ||
      props.slaDetails.sla_resolution_time_threshold,
  };

  return Number(thresholds[eventType]) || null;
};

const thresholdForEvent = event =>
  eventMetaValue(event, 'threshold_seconds') ||
  thresholdForType(event.event_type);

const measuredAtForEvent = event => {
  const { firstReplyAt, lastActivityAt, isOpen, now } = timingContext.value;
  const eventCreatedAt = Number(event.created_at);
  const metadataMeasuredAt = eventMetaValue(event, 'measured_at');

  if (event.event_type === SLA_MISS_TYPES.FRT) {
    return metadataMeasuredAt || firstReplyAt || eventCreatedAt;
  }

  if (event.event_type === SLA_MISS_TYPES.RT) {
    return isOpen
      ? now
      : lastActivityAt || metadataMeasuredAt || eventCreatedAt;
  }

  return eventCreatedAt;
};

const actualDurationForEvent = event => {
  const { createdAt } = timingContext.value;
  const measuredAt = measuredAtForEvent(event);

  if (!measuredAt) return null;

  return measuredAt - createdAt;
};

const policyDetails = computed(() => {
  const rows = [];
  const policyName =
    props.slaDetails.policy_name || props.slaDetails.sla_name || '';

  if (policyName) {
    rows.push({
      label: t('SLA.EVENTS.POLICY'),
      value: policyName,
    });
  }

  return rows;
});

const eventLabel = eventType => {
  const labels = {
    [SLA_MISS_TYPES.FRT]: t('SLA.EVENTS.FRT'),
    [SLA_MISS_TYPES.RT]: t('SLA.EVENTS.RT'),
  };

  return labels[eventType] || eventType;
};

const eventSortIndex = eventType => {
  const order = {
    [SLA_MISS_TYPES.FRT]: 1,
    [SLA_MISS_TYPES.RT]: 2,
  };

  return order[eventType] || 99;
};

const orderedSlaMissedEvents = computed(() =>
  [...props.slaMissedEvents].sort((a, b) => {
    const typeDiff =
      eventSortIndex(a.event_type) - eventSortIndex(b.event_type);
    if (typeDiff) return typeDiff;

    return Number(a.created_at) - Number(b.created_at);
  })
);

const firstEventOfType = eventType =>
  orderedSlaMissedEvents.value.find(event => event.event_type === eventType);

const resolutionMeasuredAt = event => {
  const { lastActivityAt, isOpen, now } = timingContext.value;
  if (event) return measuredAtForEvent(event);
  if (isOpen) return now;

  return lastActivityAt;
};

const timelineRows = computed(() => {
  const { createdAt, firstReplyAt } = timingContext.value;
  if (!createdAt) return [];

  const frtEvent = firstEventOfType(SLA_MISS_TYPES.FRT);
  const rtEvent = firstEventOfType(SLA_MISS_TYPES.RT);
  const frtMeasuredAt = frtEvent ? measuredAtForEvent(frtEvent) : firstReplyAt;
  const rtMeasuredAt = resolutionMeasuredAt(rtEvent);

  return [
    {
      id: 'started',
      item: t('SLA.EVENTS.TIMELINE.STARTED'),
      occurredAt: formatDate(createdAt),
      actual: '',
    },
    {
      id: 'tpr',
      item: t('SLA.EVENTS.TIMELINE.TPR'),
      occurredAt: formatDate(frtMeasuredAt),
      actual: formatDuration(frtMeasuredAt - createdAt),
    },
    {
      id: 'tr',
      item: t('SLA.EVENTS.TIMELINE.TR'),
      occurredAt: formatDate(rtMeasuredAt),
      actual: formatDuration(rtMeasuredAt - createdAt),
    },
  ];
});

const slaEventRows = computed(() =>
  orderedSlaMissedEvents.value.map(event => {
    const threshold = thresholdForEvent(event);
    const actualDuration = actualDurationForEvent(event);
    const overTarget =
      actualDuration !== null && threshold && actualDuration > threshold
        ? actualDuration - threshold
        : null;

    return {
      id: event.id,
      item: eventLabel(event.event_type),
      expected: formatThreshold(threshold),
      actual: formatDuration(actualDuration),
      occurredAt: formatDate(measuredAtForEvent(event)),
      overTarget: formatDuration(overTarget),
    };
  })
);
</script>

<template>
  <div
    class="absolute flex flex-col items-start border-n-strong bg-n-solid-3 w-[44rem] max-w-[calc(100vw-2rem)] backdrop-blur-[100px] px-5 py-4 z-50 shadow rounded-xl gap-3 overflow-visible"
  >
    <span class="text-sm font-medium text-n-slate-12">
      {{ $t('SLA.EVENTS.TITLE') }}
    </span>
    <div class="w-full">
      <table class="w-full text-sm">
        <caption class="pb-2 text-left font-medium text-n-slate-12">
          {{
            $t('SLA.EVENTS.TIMELINE.TITLE')
          }}
        </caption>
        <thead>
          <tr class="text-left text-n-slate-11 rtl:text-right">
            <th class="pb-2 pr-3 font-medium">
              {{ $t('SLA.EVENTS.TABLE.ITEM') }}
            </th>
            <th class="pb-2 px-3 font-medium text-right">
              {{ $t('SLA.EVENTS.TABLE.OCCURRED_AT') }}
            </th>
            <th class="pb-2 pl-3 font-medium text-right">
              {{ $t('SLA.EVENTS.TABLE.ACTUAL') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in timelineRows"
            :key="row.id"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="py-1.5 pr-3 whitespace-nowrap">
              {{ row.item }}
            </td>
            <td class="py-1.5 px-3 text-right whitespace-nowrap">
              {{ row.occurredAt || $t('SLA.EVENTS.NOT_AVAILABLE') }}
            </td>
            <td class="py-1.5 pl-3 text-right whitespace-nowrap">
              {{ row.actual || $t('SLA.EVENTS.NOT_AVAILABLE') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="w-full pt-3 border-t border-n-weak">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('SLA.EVENTS.BREACHED_SLA') }}
      </span>
      <div v-if="policyDetails.length" class="flex flex-col w-full gap-2 py-2">
        <div
          v-for="detail in policyDetails"
          :key="detail.label"
          class="flex justify-between w-full gap-4 text-sm"
        >
          <span class="min-w-[140px] text-n-slate-11">
            {{ detail.label }}
          </span>
          <span class="text-right text-n-slate-12">
            {{ detail.value }}
          </span>
        </div>
      </div>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-n-slate-11 rtl:text-right">
            <th class="pb-2 pr-3 font-medium">
              {{ $t('SLA.EVENTS.TABLE.ITEM') }}
            </th>
            <th class="pb-2 px-3 font-medium text-right">
              {{ $t('SLA.EVENTS.TABLE.EXPECTED') }}
            </th>
            <th class="pb-2 px-3 font-medium text-right">
              {{ $t('SLA.EVENTS.TABLE.ACTUAL') }}
            </th>
            <th class="pb-2 px-3 font-medium text-right">
              {{ $t('SLA.EVENTS.TABLE.OCCURRED_AT') }}
            </th>
            <th class="pb-2 pl-3 font-medium text-right">
              {{ $t('SLA.EVENTS.TABLE.OVER_TARGET') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in slaEventRows"
            :key="row.id"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="py-1.5 pr-3 whitespace-nowrap">
              {{ row.item }}
            </td>
            <td class="py-1.5 px-3 text-right whitespace-nowrap">
              {{ row.expected || $t('SLA.EVENTS.NOT_AVAILABLE') }}
            </td>
            <td class="py-1.5 px-3 text-right whitespace-nowrap">
              {{ row.actual || $t('SLA.EVENTS.NOT_AVAILABLE') }}
            </td>
            <td class="py-1.5 px-3 text-right whitespace-nowrap">
              {{ row.occurredAt || $t('SLA.EVENTS.NOT_AVAILABLE') }}
            </td>
            <td
              class="py-1.5 pl-3 text-right whitespace-nowrap"
              :class="row.overTarget ? 'font-medium text-n-ruby-11' : ''"
            >
              {{ row.overTarget || $t('SLA.EVENTS.NOT_AVAILABLE') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
